import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { isDatasetFacts } from '@/shared/api/generated/catalog';
import type { DatasetFacts } from '@/shared/api/generated/catalog';
import { getPage } from '@/shared/api/http';

/* The catalogue drill-down's two aggregate reads.
 *
 * Both exist to avoid the same thing: a screen that renders one row per source, or one row per
 * dataset, and then issues a request per row. That N+1 is invisible on a demo tenant with one source
 * and eight tables, and it is what makes the first screen of a drill-down unusable on a real estate.
 *
 * **Neither hook fabricates a zero.** `scored` and both profile figures are nullable on the wire and
 * stay nullable here, and an unprofiled dataset is *absent* from the facts map rather than present
 * with zeros. Every consumer therefore has to decide what to render for "we did not measure this",
 * which is the point — see `entities/catalogue` for the words each case gets.
 */

/* **Moved to `@/entities/connection` and re-exported here**, so this feature's screens read
 * unchanged.
 *
 * It went down a layer when the connections table needed the same harvest progress this grid already
 * rendered. `features/connections` importing this barrel would have closed a cycle — that feature
 * exports `ConnectionDetail`, which renders `CatalogBrowser` — and ESLint has no `import/no-cycle`
 * rule, so the failure would not have been a build error but two barrels initialising each other and
 * an `undefined` component at render time in whichever screen evaluated first. The same move
 * `useConnections` already made, for the same reason.
 *
 * A *second* hook over the same URL was the alternative and is worse than a cycle: two cache keys for
 * one endpoint is two answers to "how far has this harvest got", on two screens a reader moves
 * between in one click.
 */
export { SOURCES_PER_PAGE, useConnectionSummaries } from '@/entities/connection';

/** Rows scanned and last-profiled, keyed by dataset id, for the datasets a listing is rendering.
 *
 * **The map is what the caller wants and the array is not**, so the shaping happens here rather than
 * in three screens: a listing has a dataset id in hand and needs one lookup per row.
 *
 * **A dataset missing from the map was never profiled.** That is the wire contract and it survives
 * into this type deliberately — returning a `DatasetFacts` full of nulls for an id nobody profiled
 * would let a caller render "0 rows" for a table nobody has looked at, which is the
 * absence-is-not-zero defect this codebase holds at ten sites.
 *
 * Disabled on an empty list rather than called with none: the route requires at least one id and
 * answers 422 without, so an enabled query would report an error while a listing was simply still
 * loading. `enabled` is what turns that into "no facts yet", which is the truth.
 */
export function useDatasetFacts(
  datasetIds: readonly string[],
): UseQueryResult<ReadonlyMap<string, DatasetFacts>> {
  return useQuery({
    /* The ids are in the key, sorted, so two screens rendering the same page share one cache entry
     * whatever order their listings arrived in — and a different page is a different entry rather
     * than a stale answer under a new question. */
    queryKey: ['catalog', 'dataset-facts', [...datasetIds].sort()],
    queryFn: async () => {
      const params = new URLSearchParams();
      for (const id of datasetIds) params.append('dataset_id', id);
      const page = await getPage(
        `/v1/catalog/datasets/facts?${params.toString()}`,
        isDatasetFacts,
        'dataset facts',
      );
      return new Map(page.items.map((facts) => [facts.dataset_id, facts]));
    },
    enabled: datasetIds.length > 0,
    retry: false,
  });
}
